'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { AlertTriangle, X } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/shadcn/alert';
import { Button } from '@/shared/ui/shadcn/button';

interface RateLimitWarningProps {
  retryAfter: number;
  onDismiss?: () => void;
}

export default function RateLimitWarning({
  retryAfter,
  onDismiss,
}: RateLimitWarningProps) {
  const t = useTranslations('rateLimitWarning');
  const [countdown, setCountdown] = useState(retryAfter);

  useEffect(() => {
    if (countdown <= 0) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          onDismiss?.();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [countdown, onDismiss]);

  if (countdown <= 0) return null;

  return (
    <div className="fixed bottom-4 right-4 max-w-md w-full z-50">
      <Alert className="shadow-lg">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>{t('title')}</AlertTitle>
        <AlertDescription>
          {t('description', { count: countdown })}
        </AlertDescription>
        {onDismiss && (
          <Button
            onClick={onDismiss}
            variant="ghost"
            size="sm"
            className="absolute top-2 right-2 h-auto p-1"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </Alert>
    </div>
  );
}
